import { faSync } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState, useCallback } from 'react';

import Badge from '@/components/Badge';
import Button from '@/components/Button';
import Card from '@/components/Card';
import EmptyState from '@/components/EmptyState';
import IconicButton from '@/components/IconicButton';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { Body } from '@/components/Typography';
import useMediaQuery from '@/hooks/useMediaQuery';
import { jobColors } from '@/styles/colors';
import UniformRowItem from './UniformRowItem';
import {
  acceptUniform,
  fetchPendingUniforms,
  rejectUniform,
  WorkerUniform,
} from './api';

const HEADER_FIELDS = [
  'Worker',
  'Image',
  'Skill',
  'Submitted',
  'Status',
  'Actions',
];

const PendingUniforms = () => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [uniforms, setUniforms] = useState<WorkerUniform[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState<number | null>(null);
  const [isRejecting, setIsRejecting] = useState<number | null>(null);

  const loadUniforms = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchPendingUniforms();
      if (response.success) {
        setUniforms(response.uniforms);
      } else {
        setError('Failed to load uniforms');
      }
    } catch (err: any) {
      console.error('Error loading uniforms:', err);
      setError(err.message || 'Failed to load uniforms. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUniforms();
  }, [loadUniforms]);

  const handleAccept = async (id: number, comment?: string) => {
    setIsAccepting(id);
    try {
      await acceptUniform(id, comment);
      // Remove from list after successful acceptance
      setUniforms((prev) => prev.filter((u) => u.id !== id));
    } catch (err: any) {
      console.error('Error accepting uniform:', err);
      throw err;
    } finally {
      setIsAccepting(null);
    }
  };

  const handleReject = async (id: number, comment: string) => {
    setIsRejecting(id);
    try {
      await rejectUniform(id, comment);
      // Remove from list after successful rejection
      setUniforms((prev) => prev.filter((u) => u.id !== id));
    } catch (err: any) {
      console.error('Error rejecting uniform:', err);
      throw err;
    } finally {
      setIsRejecting(null);
    }
  };

  const handleActionComplete = () => {
    // Optionally refresh the list
    loadUniforms();
  };

  return (
    <Page
      headerPadding={!!phoneOnly}
      noPadding={!!phoneOnly}
      size={phoneOnly ? 'full' : 'lg'}
      title="Pending Uniforms"
    >
      <Card noRadius={phoneOnly}>
        <Card.Section>
          <Stack justify="apart" align="center">
            <Stack gap={12} align="center">
              <Body weight="semibold">Pending Uniforms</Body>
              {uniforms.length > 0 && (
                <Badge label={uniforms.length.toString()} status="warning" />
              )}
            </Stack>
            <IconicButton
              a11yLabel="Refresh list"
              appearance="plain"
              icon={faSync}
              onClick={loadUniforms}
              size="sm"
            />
          </Stack>
        </Card.Section>
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <Card.Section>
            <Stack vertical align="center" gap={16}>
              <Body color="danger">{error}</Body>
              <Button a11yLabel="Retry" label="Retry" onClick={loadUniforms} />
            </Stack>
          </Card.Section>
        ) : uniforms.length === 0 ? (
          <EmptyState
            title="No Pending Uniforms"
            text="All uniform submissions have been processed."
          />
        ) : (
          <Table>
            {!phoneOnly && (
              <TableHeader
                fields={HEADER_FIELDS}
                headerBgColor={jobColors.headerLight}
              />
            )}
            <tbody>
              {uniforms.map((uniform) => (
                <UniformRowItem
                  key={uniform.id}
                  uniform={uniform}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  isAccepting={isAccepting === uniform.id}
                  isRejecting={isRejecting === uniform.id}
                  onActionComplete={handleActionComplete}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Page>
  );
};

export default PendingUniforms;
